import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { getCategories, getProducts } from '@/api/products';
import { EmptyState, ErrorState, LoadingState } from '@/modules/shop/components/ShopStates';
import ProductCard from '@/modules/shop/components/ProductCard';
import Seo from '@/components/seo/Seo';
import { breadcrumbJsonLd } from '@/config/seo';

const PAGE_SIZE = 12;

/** Katalog: `/shop` (svi proizvodi) i `/shop/:category` (filtrirano). Paginacija ide kroz `?strana=`. */
const ShopPage = () => {
  const { category: categoryParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(Number(searchParams.get('strana') || 1) - 1, 0);
  // Stari oblik `/shop?category=slug` i dalje radi (linkovi izvan sajta).
  const category = categoryParam || searchParams.get('category') || undefined;
  const query = searchParams.get('q') || '';

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState(null);
  const [status, setStatus] = useState('loading');
  /** Ono što je kucano u polju; u URL (i na server) ide tek posle pauze u kucanju. */
  const [searchInput, setSearchInput] = useState(query);

  useEffect(() => {
    let active = true;
    getCategories()
      .then((data) => active && setCategories(data))
      .catch(() => active && setCategories([]));
    return () => {
      active = false;
    };
  }, []);

  // Polje prati URL kad se menja spolja (npr. dugme „Obriši pretragu", back/forward).
  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === query) return undefined;

    const timer = window.setTimeout(() => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (trimmed) params.set('q', trimmed);
        else params.delete('q');
        params.delete('strana');   // rezultat pretrage uvek kreće od prve strane
        return params;
      }, { replace: true });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchInput, query, setSearchParams]);

  const load = useCallback(() => {
    let active = true;
    setStatus('loading');
    getProducts({ category, q: query, page, size: PAGE_SIZE })
      .then((data) => {
        if (!active) return;
        setProducts(data);
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => {
      active = false;
    };
  }, [category, query, page]);

  useEffect(load, [load]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === category),
    [categories, category],
  );

  const goToPage = (nextPage) => {
    const params = new URLSearchParams(searchParams);
    if (nextPage <= 0) {
      params.delete('strana');
    } else {
      params.set('strana', String(nextPage + 1));
    }
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectCategory = (slug) => {
    // Pretraga preživljava promenu kategorije; paginacija se resetuje.
    const suffix = query ? `?q=${encodeURIComponent(query)}` : '';
    navigate(`${slug ? `/shop/${slug}` : '/shop'}${suffix}`);
  };

  const basePath = category ? `/shop/${category}` : '/shop';
  const pageSuffix = page > 0 ? ` — strana ${page + 1}` : '';
  const title = activeCategory
    ? `${activeCategory.name} — online prodavnica${pageSuffix}`
    : `Prodavnica — paneli, pergole, staklo i kamen${pageSuffix}`;
  const description = activeCategory?.description
    || 'Naručite dekorativne panele, pergole, staklo, nameštaj po meri i prirodni kamen uz plaćanje pouzećem.';
  // Paginirane strane imaju sopstvenu kanonsku URL (Google više ne spaja strane u prvu).
  const canonicalPath = page > 0 ? `${basePath}?strana=${page + 1}` : basePath;

  const trail = [
    { name: 'Prodavnica', path: '/shop' },
    ...(activeCategory ? [{ name: activeCategory.name, path: basePath }] : []),
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={canonicalPath}
        // Rezultati pretrage su tanak, promenljiv sadržaj — ne u indeks, ali linkove prati.
        noindex={Boolean(query)}
        jsonLd={query ? undefined : breadcrumbJsonLd(trail)}
      />

      <div data-testid="shop-page">
        <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-neutral-50">
          <div className="container-custom text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="caption text-brand-accent block mb-4"
            >
              Online prodavnica
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading text-4xl md:text-6xl font-light text-brand-primary mb-4"
            >
              {activeCategory ? activeCategory.name : 'Naši proizvodi'}
            </motion.h1>
            <p className="text-neutral-500 max-w-2xl mx-auto">{description}</p>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom">
            {/* Pretraga */}
            <div className="max-w-xl mx-auto mb-8">
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" size={14} />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Pretražite proizvode…"
                  aria-label="Pretraga proizvoda"
                  className="w-full bg-white border border-neutral-200 rounded-sm pl-11 pr-11 py-3
                             text-brand-primary placeholder:text-neutral-500 focus:outline-none
                             focus:ring-2 focus:ring-brand-accent focus:border-brand-accent transition-all"
                  data-testid="shop-search"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-neutral-500
                               hover:text-brand-primary transition-colors"
                    aria-label="Obriši pretragu"
                    data-testid="shop-search-clear"
                  >
                    <FaTimes size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Filter po kategoriji */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                type="button"
                onClick={() => selectCategory(null)}
                className={`px-5 py-2 text-sm rounded-sm border transition-colors ${
                  !category
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'border-neutral-200 text-brand-primary hover:border-brand-accent'
                }`}
                data-testid="shop-filter-all"
              >
                Sve
              </button>
              {categories.map((item) => (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => selectCategory(item.slug)}
                  className={`px-5 py-2 text-sm rounded-sm border transition-colors ${
                    category === item.slug
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'border-neutral-200 text-brand-primary hover:border-brand-accent'
                  }`}
                  data-testid={`shop-filter-${item.slug}`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            {status === 'loading' && <LoadingState label="Učitavanje proizvoda…" />}
            {status === 'error' && (
              <ErrorState message="Proizvode trenutno nije moguće učitati." onRetry={load} />
            )}

            {status === 'ready' && products?.content?.length === 0 && (
              query ? (
                <EmptyState
                  title={`Nema rezultata za „${query}"`}
                  description="Pokušajte sa drugom rečju ili pogledajte celu ponudu."
                  actionTo="/shop"
                  actionLabel="Prikaži sve proizvode"
                />
              ) : (
                <EmptyState
                  title={activeCategory ? `Nema proizvoda u kategoriji „${activeCategory.name}"` : 'Prodavnica je u pripremi'}
                  description="Uskoro dodajemo artikle. Do tada nam se javite — rado ćemo Vam poslati ponudu."
                />
              )
            )}

            {status === 'ready' && products?.content?.length > 0 && (
              <>
                {query && (
                  <p className="text-sm text-neutral-500 mb-6" data-testid="shop-result-count">
                    {products.totalElements === 1
                      ? `1 rezultat za „${query}"`
                      : `${products.totalElements} rezultata za „${query}"`}
                  </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
                  {products.content.map((product, index) => (
                    <ProductCard key={product.slug} product={product} index={index} />
                  ))}
                </div>

                {products.totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-16">
                    <button
                      type="button"
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 0}
                      className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="shop-prev-page"
                    >
                      Prethodna
                    </button>
                    <span className="text-sm text-neutral-500">
                      Strana {page + 1} od {products.totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => goToPage(page + 1)}
                      disabled={page + 1 >= products.totalPages}
                      className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="shop-next-page"
                    >
                      Sledeća
                    </button>
                  </div>
                )}
              </>
            )}

            <div className="text-center mt-16">
              <p className="text-neutral-500 mb-4">Ne vidite ono što tražite?</p>
              <Link to="/kontakt" className="btn-secondary inline-block">
                Zatražite ponudu
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShopPage;
